import { ApolloClient, ApolloQueryResult, FetchResult, MutationOptions, QueryOptions } from '@apollo/client';
import { DocumentNode, GraphQLError, GraphQLFormattedError } from 'graphql';
import { call } from 'redux-saga/effects';

import { Mutation, Query } from '@mfe/shared/schema-types';

import { reduxGraphQL } from './client';
import { ErrorsObj, FetchWithErrorsMutation, FetchWithErrorsQuery } from './types';

function parseGraphqlErrors(apiErrors: ApolloQueryResult<Query> | FetchResult<Mutation>): ErrorsObj | null {
  const { errors } = apiErrors;
  if (!errors) {
    return null;
  }

  const result: ErrorsObj = {};
  errors.forEach((err: GraphQLFormattedError | GraphQLError | any) => {
    console.log(err);
    result[err.path as unknown as string] = {
      message: err.message,
      code: err.extensions?.['code'],
    };
  });

  return result;
}

function* fetchWithErrorParsing(
  fetchFunction: ApolloClient<any>['query'] | ApolloClient<any>['mutate'],
  options: Omit<QueryOptions, 'errorPolicy'> | Omit<MutationOptions, 'errorPolicy'>
): Generator<any, FetchWithErrorsQuery | FetchWithErrorsMutation> {
  try {
    const result: any = yield call(fetchFunction, {
      ...(options as any),
      errorPolicy: 'all',
    });

    return {
      data: result.data,
      errors: parseGraphqlErrors(result),
    };
  } catch (ex: any) {
    return {
      data: null,
      errors: null,
      runtimeError: {
        message: ex.message || 'unknown error',
      },
    };
  }
}

export function* graphqlQuery(options: QueryOptions): any {
  const client = yield reduxGraphQL.getClient();
  const result: ApolloQueryResult<Query> = yield call(client.query, options);

  return result.data;
}

export function* graphqlQueryWithErrors(options: Omit<QueryOptions, 'errorPolicy'>): any {
  const client = yield reduxGraphQL.getClient();
  const result: FetchWithErrorsQuery = yield fetchWithErrorParsing(client.query, options);

  return result;
}

export function* graphqlMutation(options: MutationOptions<any, any>): any {
  const client = yield reduxGraphQL.getClient();
  const result: FetchResult<Mutation> = yield call(client.mutate, options);

  return result.data;
}

export function* graphqlMutationWithErrors(options: Omit<MutationOptions, 'errorPolicy'>): any {
  const client = yield reduxGraphQL.getClient();
  const result: FetchWithErrorsMutation = yield fetchWithErrorParsing(client.mutate, options);

  return result;
}

export function* evictFromCache({ id, fieldName }: { id: string; fieldName?: string }): any {
  const client = yield reduxGraphQL.getClient();
  client.cache.evict({ id, fieldName });
  client.cache.gc();
}

export function* readApolloCacheQueryFragment(fragment: DocumentNode): Generator<any, unknown, any> {
  const client = yield reduxGraphQL.getClient();
  const data: unknown = yield call(client.readFragment.bind(client), {
    id: 'ROOT_QUERY',
    fragment,
  });

  return data;
}

export function* writeApolloCacheQueryFragment({ fragment, data }: { fragment: DocumentNode; data: unknown }): any {
  const client = yield reduxGraphQL.getClient();
  yield call(client.writeFragment.bind(client), {
    id: 'ROOT_QUERY',
    fragment,
    data,
  });
}
